import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import Modal, { BotaoSecundario } from './Modal';

/* ============================================================================
   Escolher aplicativo na loja do Google (Managed Google Play), dentro do sistema.

   O PROBLEMA QUE ISTO RESOLVE: o cadastro de aplicativo pede o PACOTE digitado a
   mao (`com.exemplo.app`). Quem sabe o nome do app quase nunca sabe o pacote, e um
   caractere errado cria um registro que nunca chega a aparecer em aparelho nenhum -
   sem erro nenhum, porque pacote e apenas texto para o nosso banco.

   Aqui a pessoa procura pelo NOME, ve icone e descricao, escolhe, e o pacote vem
   preenchido pelo proprio Google.

   ---------------------------------------------------------------------------
   COMO FUNCIONA, e por que este arquivo tem tanto codigo de infraestrutura

   O iframe da loja NAO e um <iframe src=...>: ele exige a biblioteca `gapi` do
   Google, que faz a ponte de mensagens entre a pagina e o iframe (o Google chama
   isso de "gapi.iframes"). O fluxo e:

     1. pedimos um token ao nosso backend (`POST /google/play/token`), que chama
        `enterprises.webTokens.create` com a ORIGEM desta pagina;
     2. carregamos `https://apis.google.com/js/api.js` (uma vez por aba);
     3. `gapi.load('gapi.iframes')` e `openChild` montam o iframe;
     4. registramos `onproductselect`, que o iframe dispara com o packageName.

   O SCRIPT E CARREGADO SOB DEMANDA, e nao no index.html: ele so faz sentido nesta
   tela, e 100 kB de biblioteca do Google em toda abertura do sistema para um
   recurso usado no cadastro de aplicativo nao se justifica. O <script> fica na
   pagina depois (o Google nao oferece descarregamento), mas nao ha custo em ficar.

   ⚠️ A PAGINA PRECISA ESTAR EM HTTPS. Medido em 21/08/2026, chamando
   `webTokens.create` com varias origens:

     http://localhost:3000      RECUSADO (INVALID_VALUE em tokenSpec.parent)
     https://localhost:3000     aceito
     https://gpshub.com.br      aceito
     https://10.134.0.60:3000   aceito

   Duas coisas ficaram claras nesse teste. A primeira: o Google recusa `http://`. A
   segunda, que contraria o que este comentario dizia antes: NAO e preciso cadastrar
   origem em console nenhum - ele aceitou um IP de rede local que ninguem autorizou.

   Por isso a checagem de protocolo acontece AQUI, antes de qualquer chamada: o erro
   do Google para esse caso e um INVALID_VALUE com pilha Java de ~25 linhas, que nao
   menciona https. Deixar a tela explicar em uma frase vale mais do que repassar a
   pilha e esperar que alguem a decifre.
   ============================================================================ */

declare global {
  interface Window {
    gapi?: any;
  }
}

const URL_GAPI = 'https://apis.google.com/js/api.js';

/** Carrega o api.js uma vez por aba. Chamadas simultaneas esperam a mesma promessa. */
let promessaGapi: Promise<void> | null = null;

function carregarGapi(): Promise<void> {
  if (window.gapi?.load) return Promise.resolve();
  if (promessaGapi) return promessaGapi;

  promessaGapi = new Promise<void>((resolver, rejeitar) => {
    const existente = document.querySelector<HTMLScriptElement>(`script[src="${URL_GAPI}"]`);
    if (existente) {
      existente.addEventListener('load', () => resolver());
      existente.addEventListener('error', () => rejeitar(new Error('falha ao carregar api.js')));
      return;
    }
    const script = document.createElement('script');
    script.src = URL_GAPI;
    script.async = true;
    script.onload = () => resolver();
    script.onerror = () => {
      promessaGapi = null; // permite tentar de novo
      rejeitar(new Error('falha ao carregar api.js'));
    };
    document.head.appendChild(script);
  });

  return promessaGapi;
}

export default function SeletorPlayStore({
  aberto,
  onFechar,
  onEscolher,
}: {
  aberto: boolean;
  onFechar: () => void;
  /** Recebe o pacote escolhido (e o nome, quando o iframe manda). */
  onEscolher: (pacote: string, nome?: string) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const caixa = React.useRef<HTMLDivElement | null>(null);

  /* Ref com os callbacks atuais - ver o comentario das dependencias do efeito. */
  const refCallbacks = React.useRef({ onEscolher, onFechar });
  refCallbacks.current = { onEscolher, onFechar };
  const [carregando, setCarregando] = React.useState(false);
  const [erro, setErro] = React.useState('');
  const [origem, setOrigem] = React.useState('');

  React.useEffect(() => {
    if (!aberto) return undefined;

    let vivo = true;
    setErro('');
    setCarregando(true);

    const montar = async () => {
      try {
        const origemAtual = window.location.origin;
        setOrigem(origemAtual);

        /* Barra o http antes de ir ao servidor: a resposta seria um erro do Google
           que nao diz qual e o problema real. */
        if (window.location.protocol !== 'https:')
          throw new Error(
            `A loja do Google só abre em páginas https, e esta está em ${window.location.protocol.replace(':', '')} (${origemAtual}). ` +
              'Enquanto isso, dá para informar o pacote à mão no campo do formulário.'
          );

        const { data } = await axios.post<{ token: string }>('/google/play/token', { origem: origemAtual });
        if (!vivo) return;

        const token = data?.token;
        if (!token) throw new Error('o servidor não devolveu o token');

        await carregarGapi();
        if (!vivo) return;

        await new Promise<void>((resolver) => window.gapi.load('gapi.iframes', () => resolver()));
        if (!vivo || !caixa.current) return;

        // `mode=SELECT` deixa a loja em modo de ESCOLHA - sem os botoes de
        // aprovar/gerenciar app, que alteram a configuracao da enterprise.
        const url =
          `https://play.google.com/work/embedded/search?token=${encodeURIComponent(token)}` +
          `&mode=SELECT&showsearchbox=TRUE`;

        const iframe = window.gapi.iframes.getContext().openChild({
          url,
          where: caixa.current,
          attributes: { style: 'width:100%; height:100%; border:0;', scrolling: 'yes' },
        });

        iframe.register(
          'onproductselect',
          (evento: any) => {
            /* O iframe manda `packageName` e, dependendo da pagina, tambem o nome.
               Fecho aqui mesmo: escolher um app e o fim desta tarefa, e deixar a
               loja aberta depois da escolha convida a escolher outro por engano. */
            const pacote = String(evento?.packageName || '').trim();
            if (!pacote) return;
            refCallbacks.current.onEscolher(pacote, evento?.name || evento?.title || undefined);
            refCallbacks.current.onFechar();
          },
          window.gapi.iframes.CROSS_ORIGIN_IFRAMES_FILTER
        );

        if (vivo) setCarregando(false);
      } catch (e: any) {
        if (!vivo) return;
        setCarregando(false);
        setErro(e?.response?.data?.message || e?.message || 'Não foi possível abrir a loja do Google.');
      }
    };

    montar();

    return () => {
      vivo = false;
      // Limpa o iframe: reabrir com o token anterior (que expira) mostraria a
      // loja em branco.
      if (caixa.current) caixa.current.innerHTML = '';
    };
    /* DEPENDENCIAS SO DO QUE REALMENTE REABRE A LOJA (28/09/2026).

       Aqui estavam `onEscolher`, `onFechar` e `enqueueSnackbar`. Os dois
       primeiros sao arrows INLINE no chamador (ModalNovoAplicativo), entao tem
       identidade nova a cada render do pai - e este efeito nao e barato: faz
       POST /google/play/token, carrega o gapi e monta o iframe da loja. O
       cleanup ainda apaga o conteudo da caixa.

       Resultado: com a loja ABERTA, qualquer setState do pai remontava tudo -
       a loja piscava e o texto digitado na busca do Google sumia. O gatilho
       mais provavel era o proprio debounce de 400ms que valida o pacote: quem
       digitasse e clicasse em "Escolher na loja" dentro desse intervalo perdia
       a busca logo apos abrir.

       Os callbacks passam por uma ref, que e sempre a versao atual sem entrar
       na lista de dependencias. Sobra `aberto`, que e o que de fato deve
       abrir e fechar a loja. */
  }, [aberto]);

  return (
    <Modal
      aberto={aberto}
      titulo="Escolher na loja do Google"
      descricao="Procure pelo nome. Ao escolher, o pacote é preenchido automaticamente."
      largura="max-w-[900px]"
      onFechar={onFechar}
      rodape={<BotaoSecundario onClick={onFechar}>Fechar</BotaoSecundario>}
    >
      {erro ? (
        <div className="rounded-lg border border-danger bg-danger-soft p-4 text-[12px] text-danger">
          <strong className="block">{erro}</strong>
          <span className="mt-1 block text-[11px]">
            Origem enviada: <span className="tabular-nums">{origem || '—'}</span>. A loja exige que a página
            esteja em <span className="tabular-nums">https</span>. Se ela abrir em branco mesmo em https, o
            aplicativo pode não estar disponível para a organização no Google Play gerenciado. Em qualquer
            caso, dá para informar o pacote à mão no campo do formulário.
          </span>
        </div>
      ) : (
        <>
          {carregando && (
            <p className="py-10 text-center text-[12px] text-muted-foreground">Abrindo a loja do Google...</p>
          )}

          {/* Altura fixa: o iframe da loja nao redimensiona sozinho, e sem altura
              ele nasce com 0px e a tela fica vazia. */}
          <div
            ref={caixa}
            className="h-[62vh] w-full overflow-hidden rounded-lg border border-line bg-surface-2"
            style={{ display: carregando ? 'none' : 'block' }}
          />

          <p className="text-[11px] text-faint">
            A loja mostra os aplicativos disponíveis para a sua organização no Google Play gerenciado. O
            cadastro no GPShub é feito depois, com o pacote já preenchido.
          </p>
        </>
      )}
    </Modal>
  );
}
